import React from "react";

import { el } from "date-fns/locale";
import {
  coverageOptionsURL,
  addEmployeesURL,
  selectContributionTiersURL,
  reviewEmployeeEmailsURL,
  documentsUploadPageURL,
  paymentURL,
  applicationReadyURL,
  getStepFromPath,
} from "EmployerApplication/urls";
import { quotePageName } from "QuotePage/urls";
import { Link, useHistory, useLocation } from "react-router-dom";
import { companyTokenString } from "Utils/companyToken";
import { baseUrl } from "ZeroRuby/urls";

import { useGetCurrentCompanyApplicationQuery } from "../api";
import { BodyMedium, LabelSmall } from "Cobalt/BodyText";
import { Steps, Step } from "Cobalt/Steps";
import { appendAuthParams } from "Common/TokenAuth/Utils";

const EmployerApplicationMenu = () => {
  const { data: companyApplication } = useGetCurrentCompanyApplicationQuery();
  const sections = companyApplication?.applicationStatus?.sections;
  const location = useLocation();
  const history = useHistory();

  const getStepsCompleted = () => {
    if (sections) {
      return {
        1: sections.selectPlan,
        2: sections.companyInfo,
        3: sections.employeeInfo && sections.selectContributionTiers && sections.startEmployees,
        4: sections.uploadDocuments && sections.paymentInfo,
      };
    }
    return { 1: false, 2: false, 3: false, 4: false };
  };

  const stepsCompleted = getStepsCompleted();

  const getAddEmployeeUrl = () => {
    if (sections) {
      if (!sections.employeeInfo) return addEmployeesURL;
      if (!sections.selectContributionTiers) return selectContributionTiersURL;
      if (!sections.startEmployees) return reviewEmployeeEmailsURL;
    }
    return addEmployeesURL;
  };

  const addEmployeeUrl = getAddEmployeeUrl();

  const getFinalizeAppUrl = () => {
    if (sections && !sections.uploadDocuments && !sections.paymentInfo) return documentsUploadPageURL;
    if (sections && sections.uploadDocuments && !sections.paymentInfo) return paymentURL;
    return applicationReadyURL;
  };

  const finalizeAppUrl = getFinalizeAppUrl();

  const formatUrl = (url: string) => {
    if (history) return url;
    return `${baseUrl}${companyTokenString()}${url}`;
  };

  const currentStep = () => getStepFromPath(location.pathname);

  const step1Complete = !!stepsCompleted[1];
  const step2Complete = !!stepsCompleted[2];
  const step3Complete = !!stepsCompleted[3];
  const step4Complete = !!stepsCompleted[4];

  const step4Active = currentStep() === 4 || step3Complete || step4Complete;
  const step3Active = currentStep() === 3 || step4Active || step2Complete;
  const step2Active = currentStep() === 2 || step3Active || step1Complete;

  const stepLink = (label: string, sublabel: string, url: string, isActive: boolean, currentStep: boolean) => {
    let stepLabel = null;
    let labelWeight = null;

    if (!isActive) {
      stepLabel = <BodyMedium>{label}</BodyMedium>;
    } else {
      if (currentStep) {
        labelWeight = "bold";
      } else {
        labelWeight = "normal";
      }

      stepLabel = history ? (
        <BodyMedium weight={labelWeight}>
          <Link to={url}>{label}</Link>
        </BodyMedium>
      ) : (
        <BodyMedium weight={labelWeight}>
          <a href={formatUrl(url)}>{label}</a>
        </BodyMedium>
      );
    }

    return (
      <>
        {stepLabel}
        <LabelSmall>{sublabel}</LabelSmall>
      </>
    );
  };

  return (
    <Steps>
      <Step completed>{stepLink("Browse Plans", "\u00A0", appendAuthParams(quotePageName()), true)}</Step>
      <Step completed={step2Active}>{stepLink("Configure Policy", "10 min", coverageOptionsURL, step2Active, currentStep() === 2)}</Step>
      <Step completed={step3Active}>{stepLink("Manage Employees", "15 min", addEmployeeUrl, step3Active, currentStep() === 3)}</Step>
      <Step completed={step4Active}>{stepLink("Finalize Application", "5 min", finalizeAppUrl, step4Active, currentStep() === 4)}</Step>
    </Steps>
  );
};

export default EmployerApplicationMenu;
